import { IconButton } from '@mui/material';
import { PaintBrush, Question } from '@phosphor-icons/react';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import ReactFlow, {
  Background,
  Connection,
  ControlButton,
  Controls,
  NodeMouseHandler,
  OnConnect,
  OnEdgesDelete,
  Panel,
  useReactFlow,
  useStore,
} from 'reactflow';
import styled from 'styled-components';
import { GLOBAL_NODE_ID_PREFIX } from '../../constants/globalNode';
import { useAuthStore } from '../../store/storeAuth';
import { useNotificationStore } from '../../store/storeNotifications';
import { edgeTypes, nodeTypes } from '../../util/nodeTypes';
import ContextMenu, { ContextMenuProps } from '../ContextMenu';
import ColorModal from '../CoreNodeDIsplay/ColorModal';
import MultiEdgeSelectIndicator from './MultiEdgeSelectIndicator';
import MultiSelectIndicator from './MultiSelectIndicator';
import AgentStateVisual from '../AgentStateVisual/AgentStateVisual';
import { LineTypeControl } from './LineTypeControl';
import useFlowsStore from '../../store/storeFlows';

interface ExtendNode extends Node {
  id: string;
  position?: {
    x: number;
    y: number;
  };
  selected: boolean;
  data?: any;
  // parentNode: ParentNode | null;
}

const StyledBackground = styled(Background)<{
  background: string;
}>`
  background: ${props => props.background};
  height: 100vh;
`;

const proOptions = { hideAttribution: true };

const ReactFlowBody = ({
  nodes,
  edges,
  onNodesChange,
  onEdgesChange,
  setReactFlowInstance,
  onConnect,
  onEdgeDelete,
  onEdgeUpdate,
  onEdgeUpdateStart,
  onEdgeUpdateEnd,
  onDragOver,
  onDrop,
  onDragLeave,
  updateNodeEdge,
  setUpdateNodeEdge,
  onNodeDragStop,
  user,
  isValidConnection,
  onAddNode,
  onNodeSelected,
  handlePasteNode,
  handlePasteExternal,
  onMouseMove,
  onDeleteNodes,
  readOnly,
}: any) => {
  const [type, setType] = useState<string>('paste');
  const { lineType, setLineType } = useFlowsStore(s => ({
    lineType: s.lineType,
    setLineType: s.setLineType,
  }));
  const [colors, setColors] = useState(user?.backgroundColor ?? []);
  const [colorModalOpen, setColorModalOpen] = useState(false);
  const { setAuthUser } = useAuthStore(state => state);

  const theme = user.theme;
  const { setNodes } = useReactFlow();

  const sourceHandleId = useStore(s => s.connectionStartHandle?.handleId);
  const isFlowHandle =
    ['input', 'outputFlow', 'a'].includes(sourceHandleId ?? '') ||
    ['outputHandle-', 'conditionHandle-'].some(prefix =>
      sourceHandleId?.startsWith(prefix),
    );

  const validNodes = useMemo(
    () => nodes.filter((node: any) => node !== undefined && node !== null),
    [nodes],
  );

  const selectedNodes = useMemo(
    () => validNodes.filter((node: any) => node.selected),
    [validNodes],
  );
  const selectedCount = selectedNodes.length;

  const gradientStyle = {
    background:
      colors?.length <= 1
        ? `${colors[0]}`
        : `linear-gradient(-45deg, ${colors.join(', ')})`,
  };

  const [menu, setMenu] = useState<Partial<ContextMenuProps>>({});

  const handleCopyNode = useCallback(
    (node: any) => {
      const selectedNode = validNodes.find((node: ExtendNode) => node.selected);
      if (selectedNode) {
        if (selectedNode.id && selectedNode.id.startsWith(excludedIdPrefix)) {
          return;
        }

        const { text, previewResponses, ...rest } = selectedNode;

        const nodeToCopy = {
          ...rest,
          data: {
            ...rest.data,
            text: null,
            previewResponses: null,
            promptData: null,
            previewIndex: 0,
          },
        };

        setType('copy');
        onNodeSelected(nodeToCopy);
      } else {
        console.log('No node selected.');
      }
    },
    [validNodes, onNodeSelected],
  );

  const excludedIdPrefix = GLOBAL_NODE_ID_PREFIX;

  const handleCancelSelection = useCallback(() => {
    setNodes((currentNodes: any[]) =>
      currentNodes.map((node: any) => ({
        ...node,
        selected: false,
      })),
    );
  }, [setNodes]);

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (readOnly) return;

      const activeElement = document.activeElement;
      const isInput =
        activeElement?.tagName === 'INPUT' ||
        activeElement?.tagName === 'TEXTAREA' ||
        (activeElement as HTMLElement)?.contentEditable === 'true';

      if (!isInput) {
        if (event.key === 'c' && event.metaKey) {
          handleCopyNode(event);
        } else if (event.key === 'v' && event.metaKey) {
          handlePasteNode(event);
        } else if (event.key === 'Escape' && selectedCount > 1) {
          event.preventDefault();
          handleCancelSelection();
        } else if (
          event.key === 'Delete' &&
          selectedCount > 0 &&
          onDeleteNodes
        ) {
          event.preventDefault();
          const nodesToDelete = selectedNodes.filter(
            (node: any) => !node.id?.startsWith(excludedIdPrefix),
          );
          if (nodesToDelete.length > 0) {
            onDeleteNodes(nodesToDelete);
          }
        }
      }
    },
    [
      readOnly,
      handleCopyNode,
      handlePasteNode,
      selectedCount,
      selectedNodes,
      onDeleteNodes,
      handleCancelSelection,
    ],
  );

  const handlePaste = useCallback(
    async (event: ClipboardEvent) => {
      const activeElement = document.activeElement;
      const isInput =
        activeElement?.tagName === 'INPUT' ||
        activeElement?.tagName === 'TEXTAREA' ||
        (activeElement as HTMLElement)?.contentEditable === 'true';

      if (!isInput && handlePasteExternal) {
        const wasExternalFilePaste = await handlePasteExternal(event);
        if (wasExternalFilePaste) {
          event.preventDefault();
        }
      }
    },
    [handlePasteExternal],
  );

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('paste', handlePaste);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('paste', handlePaste);
    };
  }, [handleKeyDown, handlePaste]);

  const [clickedEvent, setClickedEvent] = useState<any>(null);

  const onNodeClick: NodeMouseHandler = useCallback((_, clicked) => {
    setTimeout(() => {
      setClickedEvent(clicked);
    }, 500);
  }, []);

  useEffect(() => {
    if (clickedEvent && validNodes.length > 0) {
      setClickedEvent(null);

      setNodes(
        validNodes.map((node: any) =>
          node.id === clickedEvent.id
            ? {
                ...node,
                data: {
                  ...node.data,
                  selectedBy:
                    node?.selected === false
                      ? null
                      : node?.selected && user?.id === node?.data?.selectedBy
                        ? user.id
                        : !node?.data?.selectedBy
                          ? user?.id
                          : node?.data?.selectedBy,
                },
              }
            : node,
        ),
      );
    }
  }, [setNodes, validNodes, clickedEvent]);

  // CLICK NODE CLICK
  const onNodeContextMenu = useCallback(
    (event: React.MouseEvent, node: any) => {
      const target = event.target as HTMLElement;

      const element = target.closest('[role="presentation"]');

      const isModal = target.getAttribute('role') === 'presentation';
      const modalContent = target.closest('.MuiDialogContent-root');

      const isInputOrTextarea =
        target.tagName === 'INPUT' || target.tagName === 'TEXTAREA';
      if (isModal || isInputOrTextarea || element || modalContent) {
        setMenu({});
        setType('');
        onNodeSelected(null);
        return; // Allow the default context menu
      }
      event.preventDefault(); // Prevent default browser menu

      setType('copy');
      onNodeSelected(node);
      setMenu({
        id: node.id,
        top: event.clientY - 4,
        left: event.clientX - 4,
        type: 'copy',
      });
    },
    [onNodeSelected],
  );

  const onPaneClick = useCallback(() => {
    setMenu({});
  }, []);

  return (
    <>
      {readOnly && (
        <style>{`
        .reactflow-wrapper .react-flow__node textarea,
        .reactflow-wrapper .react-flow__node input,
        .reactflow-wrapper .react-flow__node select,
        .reactflow-wrapper .react-flow__node [contenteditable="true"] {
          pointer-events: none !important;
          user-select: none !important;
        }
      `}</style>
      )}
      <ReactFlow
        style={{
          background: theme === 'dark' ? '#475467' : '#E7F0FF',
        }}
        onlyRenderVisibleElements
        connectionLineStyle={{ strokeWidth: isFlowHandle ? 7 : 1.5 }}
        className={theme === 'dark' ? 'dark-theme' : 'light-theme'}
        nodes={validNodes}
        edges={edges}
        connectionLineType={lineType}
        onNodesChange={changes => {
          if (updateNodeEdge.initial) {
            onNodesChange(changes);

            return setUpdateNodeEdge({
              ...updateNodeEdge,
              initial: false,
            });
          }
          if (changes[0].type !== 'remove') {
            onNodesChange(changes);

            if (
              changes[0].type === 'position' &&
              changes[0].dragging === true
            ) {
              setUpdateNodeEdge({
                type: changes?.[0]?.type,
                id: changes?.[0]?.id,
                data: changes?.[0]?.position!,
                initial: false,
              });
            }
          }

          if (changes[0].type === 'remove') {
          }
        }}
        onNodeClick={onNodeClick}
        onEdgeClick={e => e.stopPropagation()}
        onEdgesChange={e => {
          onEdgesChange(e);
        }}
        snapToGrid
        onEdgesDelete={onEdgeDelete as OnEdgesDelete}
        onEdgeUpdate={onEdgeUpdate}
        onEdgeUpdateStart={onEdgeUpdateStart}
        onEdgeUpdateEnd={onEdgeUpdateEnd}
        onConnect={onConnect as OnConnect}
        nodeTypes={nodeTypes as any}
        onPointerMove={onMouseMove}
        onDragOver={onDragOver}
        onDrop={onDrop}
        onDragLeave={onDragLeave}
        onInit={setReactFlowInstance}
        isValidConnection={(params: Connection) =>
          isValidConnection(params) as boolean
        }
        onNodeDragStop={(event, node) => onNodeDragStop(node)}
        proOptions={proOptions}
        fitView
        edgeTypes={edgeTypes as any}
        elevateEdgesOnSelect
        minZoom={0.03}
        onPaneClick={onPaneClick}
        onNodeContextMenu={readOnly ? undefined : onNodeContextMenu}
        multiSelectionKeyCode={['Control', 'Shift']}
        nodesDraggable={!readOnly}
        nodesConnectable={!readOnly}
        edgesUpdatable={!readOnly}
      >
        <AgentStateVisual />
        <Controls
          position="bottom-right"
          style={{
            display: 'flex',
            background: '#fff',
            justifyContent: 'space-between',
            alignItems: 'center',
            cursor: 'pointer',
            borderRadius: '5px',
            padding: '8px',
            gap: '24px',
          }}
        >
          <ControlButton
            onClick={() => {
              setColorModalOpen(true);
            }}
          >
            <PaintBrush style={{}} size={20} />
          </ControlButton>

          <LineTypeControl lineType={lineType} setLineType={setLineType} />

          <IconButton
            id="my_custom_link"
            onClick={() => {
              // setColorModalOpen(true);
            }}
          >
            <Question weight="fill" color="#000000" size={20} />
          </IconButton>
        </Controls>

        <Panel position="top-right">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <MultiSelectIndicator
              selectedCount={selectedCount}
              selectedNodes={selectedNodes}
              onDeleteNodes={onDeleteNodes}
              onCancelSelection={handleCancelSelection}
            />
            <MultiEdgeSelectIndicator />
          </div>
        </Panel>
        <ColorModal
          colorModalOpen={colorModalOpen}
          setColorModalOpen={setColorModalOpen}
          colors={colors}
          setColors={setColors}
          setAuthUser={setAuthUser}
        />

        {/* <MiniMapStyled /> */}
        <StyledBackground
          gap={12}
          size={1}
          background={gradientStyle.background}
        />

        {menu && (
          <ContextMenu
            id={menu.id as string}
            top={menu.top || 0}
            left={menu.left || 0}
            setMenu={setMenu}
            onCopy={handleCopyNode}
            onAddNode={onAddNode}
            onPaste={() => console.log('Pasting node reactflowbody')}
            onDelete={() => console.log('Deleting node')}
            type={type}
            setType={setType}
          />
        )}
      </ReactFlow>
    </>
  );
};

export default ReactFlowBody;
